import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { CurrencyCode, DEFAULT_CURRENCY } from "./currencies";
import { useOrganization } from "./useOrganization";
import { convertCurrency } from "./currencyConversion";

export interface DisplayCurrencyContextType {
  displayCurrency: CurrencyCode;
  setDisplayCurrency: (code: CurrencyCode | null) => void;
  convertForDisplay: (
    amount: number,
    originalCurrency: CurrencyCode
  ) => { amount: number; currencyCode: CurrencyCode };
  orgDefaultCurrency: CurrencyCode;
  isLoading: boolean;
}

export const DisplayCurrencyContext = createContext<
  DisplayCurrencyContextType | undefined
>(undefined);

export const DisplayCurrencyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { organization, isLoading: isOrgLoading } = useOrganization();
  
  const orgDefault = (organization?.defaultCurrency as CurrencyCode) || DEFAULT_CURRENCY;
  
  const displayCurrency = orgDefault;

  // Retain the API, but global currency overrides are no longer supported.
  const setDisplayCurrency = useCallback(
    (code: CurrencyCode | null) => {
      if (code !== null && code !== orgDefault) {
        console.warn(
          "Global display currency cannot be changed; use DisplayCurrencyScope for dashboard reporting."
        );
      }
    },
    [orgDefault]
  );

  const convertForDisplay = useCallback(
    (amount: number, originalCurrency: CurrencyCode) => {
      return {
        amount: convertCurrency(amount, originalCurrency, displayCurrency),
        currencyCode: displayCurrency,
      };
    },
    [displayCurrency]
  );

  return (
    <DisplayCurrencyContext.Provider
      value={{
        displayCurrency,
        setDisplayCurrency,
        convertForDisplay,
        orgDefaultCurrency: orgDefault,
        isLoading: isOrgLoading,
      }}
    >
      {children}
    </DisplayCurrencyContext.Provider>
  );
};

export const DisplayCurrencyScope: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const parent = useDisplayCurrency();
  const { orgDefaultCurrency, isLoading } = parent;
  const [scopedCurrency, setScopedCurrency] = useState<CurrencyCode | null>(null);
  const displayCurrency = scopedCurrency ?? orgDefaultCurrency;

  const setDisplayCurrency = useCallback(
    (code: CurrencyCode | null) => {
      setScopedCurrency(code === orgDefaultCurrency ? null : code);
    },
    [orgDefaultCurrency]
  );

  const convertForDisplay = useCallback(
    (amount: number, originalCurrency: CurrencyCode) => ({
      amount: convertCurrency(amount, originalCurrency, displayCurrency),
      currencyCode: displayCurrency,
    }),
    [displayCurrency]
  );

  const value = useMemo<DisplayCurrencyContextType>(
    () => ({
      displayCurrency,
      setDisplayCurrency,
      convertForDisplay,
      orgDefaultCurrency,
      isLoading,
    }),
    [displayCurrency, setDisplayCurrency, convertForDisplay, orgDefaultCurrency, isLoading]
  );

  return (
    <DisplayCurrencyContext.Provider value={value}>
      {children}
    </DisplayCurrencyContext.Provider>
  );
};

export const useDisplayCurrency = (): DisplayCurrencyContextType => {
  const context = useContext(DisplayCurrencyContext);
  if (context === undefined) {
    throw new Error(
      "useDisplayCurrency must be used within a DisplayCurrencyProvider"
    );
  }
  return context;
};